import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check, Users, Loader2, Mail } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { GovernanceService } from '../services/GovernanceService';
import { useTheme } from './ThemeProvider';
import { getUserAvatar } from '../utils/userUtils';

interface JoinRequest {
    userId: string;
    requestedAt: any;
    status: 'pending' | 'approved' | 'denied';
    fromInviteCode: string | null;
    schoolEmail?: string;
    // Fetched profile fields
    userName?: string;
    userAvatar?: string;
    userUsername?: string;
}

interface JoinRequestsModalProps {
    isOpen: boolean;
    onClose: () => void;
    tribeId: string;
    tribeName: string;
    ownerId: string;
    onUpdate?: () => void;
}

export function JoinRequestsModal({ isOpen, onClose, tribeId, tribeName, ownerId, onUpdate }: JoinRequestsModalProps) {
    const { theme } = useTheme();
    const [requests, setRequests] = useState<JoinRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (!isOpen || !tribeId) return;

        let unsub: (() => void) | null = null;

        const setup = async () => {
            setLoading(true);
            const { getDbSafe } = await import('../services/firebaseConfig');
            const { collection, query, where, onSnapshot, getDoc, doc } = await import('firebase/firestore');
            const db = await getDbSafe();

            const q = query(
                collection(db, 'tribes', tribeId, 'join_requests'),
                where('status', '==', 'pending')
            );

            unsub = onSnapshot(q, async (snapshot) => {
                const raw = snapshot.docs.map(d => ({ userId: d.id, ...d.data() })) as JoinRequest[];

                // Fetch user profiles in parallel
                const enriched = await Promise.all(
                    raw.map(async (req) => {
                        try {
                            const userSnap = await getDoc(doc(db, 'users', req.userId));
                            if (userSnap.exists()) {
                                const ud = userSnap.data();
                                return {
                                    ...req,
                                    userName: ud.name || ud.username || 'Unknown',
                                    userUsername: ud.username || '',
                                    userAvatar: getUserAvatar(ud),
                                };
                            }
                        } catch {}
                        return req;
                    })
                );

                setRequests(enriched);
                setLoading(false);
            });
        };

        setup();
        return () => { if (unsub) unsub(); };
    }, [isOpen, tribeId]);

    const handleApprove = async (req: JoinRequest) => {
        setActionLoading(prev => ({ ...prev, [req.userId]: true }));
        try {
            await GovernanceService.approveJoinRequest(ownerId, tribeId, req.userId);
            toast.success(`${req.userName || 'User'} approved`);
            // onSnapshot will remove this request from the list automatically
            onUpdate?.();
        } catch (err: any) {
            toast.error(err.message || 'Failed to approve request');
        } finally {
            setActionLoading(prev => ({ ...prev, [req.userId]: false }));
        }
    };

    const handleDeny = async (req: JoinRequest) => {
        setActionLoading(prev => ({ ...prev, [req.userId]: true }));
        try {
            await GovernanceService.denyJoinRequest(ownerId, tribeId, req.userId);
            toast.info(`${req.userName || 'User'} denied`);
            onUpdate?.();
        } catch (err: any) {
            toast.error(err.message || 'Failed to deny request');
        } finally {
            setActionLoading(prev => ({ ...prev, [req.userId]: false }));
        }
    };

    const formatTime = (ts: any) => {
        try {
            const date = ts?.toDate ? ts.toDate() : new Date(ts);
            return formatDistanceToNow(date, { addSuffix: true });
        } catch {
            return 'recently';
        }
    };

    if (!isOpen) return null;

    // Render via portal to document.body so the overlay escapes the screen's
    // PageTransition transform (a stacking context that was trapping its z-index
    // behind the post-feed empty state). zIndex 999 / elevation per spec.
    return createPortal(
        <AnimatePresence>
            <div
                className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                style={{ zIndex: 999 }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
                    style={{
                        backgroundColor: theme === 'dark' ? '#1a1a1a' : '#FDFBF7',
                        minHeight: '50vh',
                        maxHeight: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        // elevation for Android/Capacitor webview layering parity
                        zIndex: 999,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-black/10 dark:border-white/10 shrink-0">
                        <div>
                            <h2 className="text-lg font-bold text-black dark:text-white">Join Requests</h2>
                            <p className="text-xs text-black/50 dark:text-white/50">{tribeName}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-black/50 dark:text-white/50"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="w-8 h-8 animate-spin text-black/30 dark:text-white/30" />
                            </div>
                        ) : requests.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-6">
                                <div className="w-14 h-14 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center">
                                    <Users className="w-7 h-7 text-black/20 dark:text-white/20" />
                                </div>
                                <p className="text-sm font-medium text-black/50 dark:text-white/50">No pending requests</p>
                                <p className="text-xs text-black/30 dark:text-white/30">New requests will appear here</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-black/5 dark:divide-white/5">
                                {requests.map((req) => (
                                    <div key={req.userId} className="px-6 py-4 flex items-center gap-4">
                                        <Avatar className="w-10 h-10 shrink-0 border border-black/10 dark:border-white/10">
                                            <AvatarImage src={req.userAvatar} className="object-cover" />
                                            <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-sm font-bold">
                                                {(req.userName || '?')[0].toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>

                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-black dark:text-white truncate">
                                                {req.userName || 'Unknown'}
                                            </p>
                                            {req.userUsername && (
                                                <p className="text-xs text-black/40 dark:text-white/40 truncate">
                                                    {req.userUsername.startsWith('@') ? req.userUsername : `@${req.userUsername}`}
                                                </p>
                                            )}
                                            {req.schoolEmail && (
                                                <p className="text-[11px] text-black/40 dark:text-white/40 truncate flex items-center gap-1 mt-0.5">
                                                    <Mail className="w-3 h-3 shrink-0" />
                                                    {req.schoolEmail}
                                                </p>
                                            )}
                                            <p className="text-[11px] text-black/30 dark:text-white/30 mt-0.5">
                                                {formatTime(req.requestedAt)}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2 shrink-0">
                                            {actionLoading[req.userId] ? (
                                                <Loader2 className="w-5 h-5 animate-spin text-black/30 dark:text-white/30" />
                                            ) : (
                                                <>
                                                    <Button
                                                        onClick={() => handleApprove(req)}
                                                        size="sm"
                                                        className="h-8 px-3 bg-green-500 hover:bg-green-600 text-white border-none text-xs font-bold"
                                                    >
                                                        <Check className="w-3.5 h-3.5 mr-1" />
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDeny(req)}
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 px-3 border-red-200 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-bold"
                                                    >
                                                        <X className="w-3.5 h-3.5 mr-1" />
                                                        Deny
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
}
